import React, { FC, ReactElement, useEffect, useState } from 'react';
import Typist from 'react-typist';
import { Button, Col, Container, Image, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styles from './Hero.module.css';
import Fade from 'react-reveal/Fade';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faPlayCircle, faFire } from '@fortawesome/free-solid-svg-icons';
import IHeroProps from '../../domain/interfaces/components/IHeroProps';
import helpers from '../../util/helpers';

const Hero: FC<IHeroProps> = (props): ReactElement => {
  const { t } = useTranslation();

  const [show, setShow] = useState<boolean>(false);
  const [isDesktop, setDesktop] = useState<boolean>(window.innerWidth > 575);

  const updateMedia = () => {
    setDesktop(window.innerWidth > 575);
  };

  useEffect(() => {
    window.addEventListener('resize', updateMedia);
    return () => window.removeEventListener('resize', updateMedia);
  });

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

 const renderOthers = () => (
    <>
      <video
        className={styles.heroVideo}
        autoPlay
        loop
        poster="/img/video_placedor.png"
        muted={process.env.NODE_ENV !== 'test'}
      >
        <source id="mp4" src="/img/hero.mp4" type="video/mp4" />
      </video>

      <Fade bottom duration={800} delay={400}>
        <h1 className={styles.heroTitle}>
          {t('components.hero.title_second')}
        </h1>
        <Typist.Delay ms={200} />
        <h6 className={styles.heroSubtitle}>Uzyskaj dostęp do konsultacji i porad naszych Ekspertów, a także skorzystaj z nagranych szkoleń wideo w dowolnym czasie</h6>
      </Fade>

      <Container>
        {/* Usunęliśmy sztywne kolumny Bootstrapa, używamy flexboxa z gap-4, co daje równy, ale mniejszy odstęp */}
        <div className="d-flex flex-row justify-content-center align-items-center flex-wrap gap-4 mt-4">
          <Fade bottom duration={800} delay={700}>
            <Button
              className={`${styles.btnConsult} d-flex align-items-center justify-content-center`}
              href={helpers.getConsultationRoute()}
            >
              <FontAwesomeIcon icon={faCommentDots} className="me-2" size="lg" style={{ color: '#303a4d' }} />
              {t('components.hero.btn_consult')}
            </Button>
          </Fade>

          <Fade bottom duration={900} delay={650}>
            {/* Zmieniamy Button na zwykły tag <a>, żeby uniknąć błędu z "zablokowanym" stanem Bootstrapa */}
            <a
              className={`${styles.btnPromo} d-flex align-items-center justify-content-center pulse-animation`}
              href="/promocja"
              style={{ textDecoration: 'none' }}
            >
              <FontAwesomeIcon icon={faFire} className="me-2" size="lg" />
              PROMOCJA
            </a>
          </Fade>

          <Fade bottom duration={1000} delay={600}>
            <Button
              className={`${styles.btnConsult} d-flex align-items-center justify-content-center`}
              href={helpers.getVideoRoute()}
            >
              <FontAwesomeIcon icon={faPlayCircle} className="me-2" size="lg" style={{ color: '#303a4d' }} />
              {t('components.hero.btn_video')}
            </Button>
          </Fade>
        </div>
      </Container>
    </>
  );

  const renderMobile = () => (
    <div className={styles.mobileHeroContainer}>
      <div className={styles.mobileContentWrapper}>
        <h1 className={styles.mobileHeroTitle}>
          {t('components.hero.title_second', 'Wiedza ekspercka dostępna cały czas')}
        </h1>

        <div className={styles.mobileVideoWrapper} onClick={handleShow}>
          <Image loading="lazy" src="/img/pc_video.png" className="img-fluid" alt="Podgląd szkolenia wideo" />
        </div>

        {/* Modal wideo pozostaje bez zmian */}
        <Modal show={show} onHide={handleClose} dialogClassName="modal-dialog-centered" contentClassName="bg-transparent border-0">
          <Modal.Body className="p-0">
            <video className="w-100 rounded-3 shadow-lg" autoPlay loop playsInline poster="/img/video_placedor.png" muted>
              <source id="mp4" src="/img/hero.mp4" type="video/mp4" />
            </video>
          </Modal.Body>
        </Modal>

        {/* Odwrócona piramida na mobile */}
        <div className="d-flex flex-column align-items-center gap-3 w-100 px-3 mt-3">
          {/* Rząd 1: Konsultacje i Nagrania obok siebie */}
          <div className="d-flex flex-row justify-content-center gap-3" style={{ width: '80%' }}>
            <Button
              className={`${styles.btnConsultMobile} d-flex align-items-center justify-content-center flex-grow-1`}
              href={helpers.getConsultationRoute()}
            >
              <FontAwesomeIcon icon={faCommentDots} className="me-2" />
              {t('components.hero.btn_consult')}
            </Button>

            <Button
              className={`${styles.btnVideo} d-flex align-items-center justify-content-center flex-grow-1`}
              href={helpers.getVideoRoute()}
            >
              <FontAwesomeIcon icon={faPlayCircle} className="me-2" />
              {t('components.hero.btn_video')}
            </Button>
          </div>

          {/* Rząd 2: Promocja na środku pod nimi */}
          <a
            className={`${styles.btnPromoMobile} d-flex align-items-center justify-content-center pulse-animation`}
            href="/promocja"
            style={{ width: '50%', textDecoration: 'none' }}
          >
            <FontAwesomeIcon icon={faFire} className="me-2" />
            PROMOCJA
          </a>
        </div>

        {renderScrollAnimation(true)}
      </div>
    </div>
  );

  const renderScrollAnimation = (isMobile: boolean) => {
    return (
      <div className={styles.container}>
        <div className={`${styles.chevron} ${isMobile ? styles.mobile : ''}`}></div>
        <div className={`${styles.chevron} ${isMobile ? styles.mobile : ''}`}></div>
        <div className={`${styles.chevron} ${isMobile ? styles.mobile : ''}`}></div>
      </div>
    );
  };

 return (
    <div
      className={`${styles.heroWrapper} ${isDesktop ? 'd-flex flex-column justify-content-center' : ''}`}
    >
      {isDesktop ? (
        <>
          <div>{renderOthers()}</div>
          <div className="d-flex align-items-center justify-content-center mt-5">
            {renderScrollAnimation(false)}
          </div>
        </>
      ) : (
        renderMobile()
      )}
    </div>
  );
};

export default Hero;